import { ChakraBox, FAIconName, TextInputV2 } from '@equip.health/ui';
import { AddProblemModalProblemButton } from '~/components/maud/patients/profile/tabs/careplan/AddProblemModalProblemButton';
import { Dispatch, SetStateAction, useMemo, useState } from 'react';
import { useGetRefCarePlanProblemsEnumQuery } from '~/store/services/refCarePlanEnums';
import { useGetPatientDiagnosesQuery } from '~/store/services/diagnoses';
import { ProblemTypeCode } from '~/lib/types/carePlan/ProblemTypeCode';
import Loading from '~/pages/loading';
import { RefCarePlanProblemResponse } from '~/lib/types/carePlan/RefCarePlanProblemResponse';
import { CarePlanStatus } from '~/lib/types/carePlan/CarePlanStatus';
import { useAnalytics } from '~/lib/contexts/AnalyticsContext';
import { CarePlanEvents } from '~/analytics/carePlanEvents';
import { useGetCarePlansByTreatmentEpisodeIdQuery } from '~/store/services/carePlan';

type Props = {
  patientId: string;
  problemTypeCode: ProblemTypeCode;
  setSelectedProblem: Dispatch<SetStateAction<RefCarePlanProblemResponse>>;
  treatmentEpisodeId: string;
};
export const AddProblemModalSelectProblemStep = ({
  patientId,
  problemTypeCode,
  setSelectedProblem,
  treatmentEpisodeId,
}: Props) => {
  const { trackEvent } = useAnalytics();

  const { data: patientDiagnoses, isLoading: isGetPatientDiagnosesLoading } =
    useGetPatientDiagnosesQuery(patientId);

  const activeDiagnosisLabels = (patientDiagnoses || [])
    ?.filter((diagnosis) => diagnosis.active)
    .map((diagnosis) => diagnosis.diagnosis.code);

  // must be filtered by problem type
  const {
    data: problemsForDiagnosis,
    isLoading: isGetProblemsForDiagnosisLoading,
  } = useGetRefCarePlanProblemsEnumQuery(
    {
      diagnosisCodeLabels: activeDiagnosisLabels,
    },
    { skip: isGetPatientDiagnosesLoading },
  );

  const { data: problems, isLoading: isGetRefProblemsLoading } =
    useGetRefCarePlanProblemsEnumQuery({});

  const { data: carePlans } =
    useGetCarePlansByTreatmentEpisodeIdQuery(treatmentEpisodeId);

  const [searchText, setSearchText] = useState('');

  const activeCarePlan = carePlans?.find(
    (pathway) => pathway.status.code === CarePlanStatus.ACTIVE,
  );
  const existingProblemCodes =
    activeCarePlan?.problems.map((problem) => problem.pathwayProblem.code) ||
    [];

  const filteredDiagnosisProblems = useMemo(
    () =>
      (problemsForDiagnosis || [])
        .filter((problem) => !existingProblemCodes?.includes(problem.code))
        .filter((problem) => problem.problemType.code === problemTypeCode)
        .filter(
          (problem) => problem.label.toLowerCase().includes(searchText.toLowerCase()),
        )
        .sort((p1, p2) => p1.sortOrder - p2.sortOrder),
    [problemsForDiagnosis, problemTypeCode, searchText],
  );

  const filteredProblemsExcludingDiagnosesProblems = useMemo(
    () =>
      (problems || [])
        .filter((problem) => !existingProblemCodes?.includes(problem.code))
        .filter((problem) => problem.problemType.code === problemTypeCode)
        .filter(
          (problem) => problem.label.toLowerCase().includes(searchText.toLowerCase()),
        )
        .filter(
          (problem) =>
            !filteredDiagnosisProblems.some(
              (diagnosisProblem) => diagnosisProblem.code === problem.code,
            ),
        ),
    [problemTypeCode, problems, filteredDiagnosisProblems, searchText],
  );

  const onProblemClick = (problem: RefCarePlanProblemResponse) => {
    setSelectedProblem(problem);
    trackEvent(CarePlanEvents.clickOnProblemMenuOptionInAddProblemModal);
  };

  if (
    isGetPatientDiagnosesLoading ||
    isGetRefProblemsLoading ||
    isGetProblemsForDiagnosisLoading
  ) {
    return <Loading />;
  }

  return (
    <>
      <ChakraBox marginBottom="24px">
        <TextInputV2
          id="add-problem-modal-search-input"
          leftIcon={FAIconName.magnifyingGlassRegular}
          onChange={(newText) => setSearchText(newText)}
          onFocus={() =>
            trackEvent(CarePlanEvents.clickInSearchProblemsSearchField)
          }
          placeholder="Search problems"
          value={searchText}
        />
      </ChakraBox>
      {filteredDiagnosisProblems.map((problem) => (
        <AddProblemModalProblemButton
          isRecommended
          key={`add-problem-modal-${problem.code}`}
          onClick={() => onProblemClick(problem)}
          problem={problem}
        />
      ))}
      {filteredProblemsExcludingDiagnosesProblems.map((problem) => (
        <AddProblemModalProblemButton
          key={`add-problem-modal-${problem.code}`}
          onClick={() => onProblemClick(problem)}
          problem={problem}
        />
      ))}
    </>
  );
};
